/**
 * 助手设置（与 iOS AssistantSettings / ParentAssistant 的 Key 部分一致）：
 * - 家长端自己的对话服务、模型、思考等级存在 localStorage（只影响这个浏览器）；
 * - 家庭共用的 Key 存在服务端（加密），GET assistant/credentials 取到后只放在内存里，不写进浏览器存储。
 */
import { api, ApiError, createStore, useStoreOf } from "../../core";
import { customProvider, DASHSCOPE, DEEPSEEK, resolveModel, type ChatProvider, type Option, PARENT_QWEN_MODELS, DEEPSEEK_MODELS, QWEN_MODELS } from "./providers";

// ---------- 家长端的选择（localStorage） ----------

export interface ParentPrefs {
  /** dashscope / deepseek / custom */
  provider: string;
  model: string;
  /** off / low / medium / high */
  thinking: string;
}

const PREFS_KEY = "kp.web.assistant.parent";
const DEFAULT_PREFS: ParentPrefs = { provider: "dashscope", model: "qwen-plus", thinking: "off" };

function readPrefs(): ParentPrefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (raw) return { ...DEFAULT_PREFS, ...(JSON.parse(raw) as Partial<ParentPrefs>) };
  } catch {
    // 隐私模式等
  }
  return { ...DEFAULT_PREFS };
}

export const prefsStore = createStore<ParentPrefs>(readPrefs());

export function setPrefs(patch: Partial<ParentPrefs>): void {
  prefsStore.setState(patch);
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefsStore.getState()));
  } catch {
    // 忽略
  }
}

export function usePrefs(): ParentPrefs {
  return useStoreOf(prefsStore, (s) => s);
}

// ---------- 家庭共用的 Key（内存） ----------

export interface CustomConfig {
  name: string;
  baseUrl: string;
  apiKey: string;
  models: string[];
}

export interface Credentials {
  apiKey: string;
  deepseekApiKey: string | null;
  custom: CustomConfig | null;
}

export interface CredState {
  /** null：还没取过；false：服务端没有百炼 Key */
  creds: Credentials | false | null;
}

export const credStore = createStore<CredState>({ creds: null });

export function useCreds(): Credentials | false | null {
  return useStoreOf(credStore, (s) => s.creds);
}

/** 从服务端取家庭共用的 Key；没有百炼 Key（404）或无权限（403）时记为 false */
export async function refreshCredentials(): Promise<Credentials | false | null> {
  try {
    const c = await api.get<Credentials>("assistant/credentials");
    credStore.setState({ creds: { apiKey: c.apiKey, deepseekApiKey: c.deepseekApiKey ?? null, custom: c.custom ?? null } });
  } catch (e) {
    if (e instanceof ApiError && (e.status === 404 || e.status === 403)) credStore.setState({ creds: false });
    else console.warn("[assistant] 获取 Key 失败", e instanceof Error ? e.message : e);
  }
  return credStore.getState().creds;
}

export function clearCredentials(): void {
  credStore.setState({ creds: null });
}

/** 当前选的对话服务；选了自定义但没有配置时退回百炼（customMissing = true） */
export function chatRoute(prefs: ParentPrefs, creds: Credentials | false | null): { provider: ChatProvider; model: string; key: string | null; customMissing: boolean } {
  const c = creds || null;
  let provider: ChatProvider = DASHSCOPE;
  let key: string | null = c?.apiKey ?? null;
  let customMissing = false;
  if (prefs.provider === "deepseek") {
    provider = DEEPSEEK;
    key = c?.deepseekApiKey ?? null;
  } else if (prefs.provider === "custom") {
    const p = c?.custom ? customProvider(c.custom.name, c.custom.baseUrl, c.custom.models) : null;
    if (p) {
      provider = p;
      key = c!.custom!.apiKey || null;
    } else {
      customMissing = true;
    }
  }
  return { provider, model: resolveModel(provider, prefs.model), key, customMissing };
}

// ---------- Key 状态（设置页） ----------

export interface KeyStatus {
  configured: boolean;
  masked?: string | null;
  updatedAt?: number | null;
  /** 自定义服务的名称、地址、模型（不含 Key） */
  name?: string | null;
  baseUrl?: string | null;
  models?: string[] | null;
}

export async function fetchKeyStatuses(): Promise<Record<string, KeyStatus>> {
  const r = await api.get<{ providers: Record<string, KeyStatus> }>("family/assistant-key");
  return r.providers ?? {};
}

/** 保存百炼（dashscope）或 DeepSeek 的 Key */
export async function saveKey(provider: "dashscope" | "deepseek", apiKey: string): Promise<void> {
  await api.put("family/assistant-key", { provider, apiKey });
  await refreshCredentials();
}

/** 保存自定义服务；apiKey 不填时沿用原来的 */
export async function saveCustom(body: { name: string; baseUrl: string; apiKey?: string; models: string[] }): Promise<void> {
  await api.put("family/assistant-key", { provider: "custom", ...body });
  await refreshCredentials();
}

export async function deleteKey(provider: string): Promise<void> {
  await api.del("family/assistant-key", { query: { provider } });
  await refreshCredentials();
}

// ---------- 选项 ----------

/** 可选的对话服务：自定义服务配置了才出现 */
export function providerOptions(customStatus: KeyStatus | undefined): Option[] {
  const list: Option[] = [
    { id: "dashscope", name: DASHSCOPE.name },
    { id: "deepseek", name: DEEPSEEK.name },
  ];
  if (customStatus?.configured) list.push({ id: "custom", name: customStatus.name || "自定义" });
  return list;
}

/** 某个对话服务的模型；家长端的百炼另外可以选看图模型 */
export function modelOptions(provider: string, parent: boolean, customStatus: KeyStatus | undefined): Option[] {
  if (provider === "deepseek") return DEEPSEEK_MODELS;
  if (provider === "custom") return (customStatus?.models ?? []).map((m) => ({ id: m, name: m }));
  return parent ? PARENT_QWEN_MODELS : QWEN_MODELS;
}

/** 自定义服务的模型列表输入（逗号、顿号、空格、换行分开） */
export function parseModelList(s: string): string[] {
  return s
    .split(/[,，、\s]+/)
    .map((x) => x.trim())
    .filter(Boolean);
}
